import { CURRENCY_SYMBOLS } from "@/shared/common/static";

export type TaxConfig = {
  label: string;
  rate: number;
  percentage: number;
};

export type CountryLocalisationConfig = {
  country: string;
  aliases?: string[];
  currencyCode: string;
  currencySymbol?: string;
  mobileCountryCode: string;
  mobileCountryIso: string;
  preferLocalCurrencyForDisplay?: boolean;
  tax: TaxConfig;
};

const DEFAULT_COUNTRY_KEY = "UAE";

const COUNTRY_CONFIG_MAP: Record<string, CountryLocalisationConfig> = {
  UAE: {
    country: "UAE",
    aliases: ["AE", "ARE"],
    currencyCode: "AED",
    currencySymbol: "AED",
    mobileCountryCode: "971",
    mobileCountryIso: "AE",
    preferLocalCurrencyForDisplay: true,
    tax: {
      label: "VAT",
      rate: 0.05,
      percentage: 5,
    },
  },
  IND: {
    country: "IND",
    aliases: ["IN", "INDIA"],
    currencyCode: "INR",
    currencySymbol: "₹",
    mobileCountryCode: "91",
    mobileCountryIso: "IN",
    preferLocalCurrencyForDisplay: true,
    tax: {
      label: "GST",
      rate: 0.18,
      percentage: 18,
    },
  },
  SAU: {
    country: "SAU",
    aliases: ["SA", "SAUDI", "KSA"],
    currencyCode: "SAR",
    currencySymbol: "SAR",
    mobileCountryCode: "966",
    mobileCountryIso: "SA",
    preferLocalCurrencyForDisplay: true,
    tax: {
      label: "VAT",
      rate: 0.15,
      percentage: 15,
    },
  },
};

const normalizeCountryFromEnv = (value?: string): string => {
  const normalized = String(value || "")
    .trim()
    .toUpperCase();

  if (!normalized) {
    return DEFAULT_COUNTRY_KEY;
  }

  if (COUNTRY_CONFIG_MAP[normalized]) {
    return normalized;
  }

  const resolvedKey = Object.entries(COUNTRY_CONFIG_MAP).find(([, config]) =>
    [config.country, ...(config.aliases || [])]
      .map((entry) => entry.toUpperCase())
      .includes(normalized)
  )?.[0];

  return resolvedKey || DEFAULT_COUNTRY_KEY;
};

const resolvedCountry = normalizeCountryFromEnv(import.meta.env.VITE_APP_LOCALISATION_COUNTRY);

export const LOCALISATION_CONFIG = COUNTRY_CONFIG_MAP[resolvedCountry];

export const getDisplayCurrencyCode = (inputCurrency?: string) => {
  const localCurrency = String(LOCALISATION_CONFIG.currencyCode || "").toUpperCase();

  if (LOCALISATION_CONFIG.preferLocalCurrencyForDisplay) {
    return localCurrency;
  }

  const normalizedInput = String(inputCurrency || "")
    .trim()
    .toUpperCase();

  return normalizedInput || localCurrency;
};

export const getLocaleTag = () => {
  const countryIso = String(LOCALISATION_CONFIG.mobileCountryIso || "")
    .trim()
    .toUpperCase();

  return countryIso ? `en-${countryIso}` : "en";
};

export const getCurrencySymbol = (inputCurrency?: string) => {
  const currencyCode = getDisplayCurrencyCode(inputCurrency || LOCALISATION_CONFIG.currencyCode);
  if (currencyCode === LOCALISATION_CONFIG.currencyCode && LOCALISATION_CONFIG.currencySymbol) {
    return LOCALISATION_CONFIG.currencySymbol;
  }
  return CURRENCY_SYMBOLS[currencyCode] || currencyCode;
};

export const formatLocalizedCurrency = (amount: number, inputCurrency?: string) => {
  const currencyCode = getDisplayCurrencyCode(inputCurrency || LOCALISATION_CONFIG.currencyCode);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(amount) ? amount : 0);
};

type CurrencyLabelOptions = {
  inputCurrency?: string;
  locale?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

export const formatCurrencyLabel = (amount: number, options: CurrencyLabelOptions = {}) => {
  const {
    inputCurrency,
    locale = "en-IN",
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
  } = options;

  const currencyCode = getDisplayCurrencyCode(inputCurrency || LOCALISATION_CONFIG.currencyCode);
  const numericAmount = Number.isFinite(amount) ? amount : 0;
  const formattedAmount = numericAmount.toLocaleString(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return `${currencyCode} ${formattedAmount}`;
};

export const getTaxLabelWithRate = () =>
  `${LOCALISATION_CONFIG.tax.label} (${LOCALISATION_CONFIG.tax.percentage}%)`;

export const getExcludingTaxLabel = () => `Excl. ${LOCALISATION_CONFIG.tax.label}`;

export const getHomeCountryLabel = () => {
  const countryKey = String(LOCALISATION_CONFIG.country || "")
    .trim()
    .toUpperCase();

  if (countryKey === "IND") {
    return "India";
  }

  if (countryKey === "SAU") {
    return "Saudi Arabia";
  }

  return "United Arab Emirates";
};
